import * as React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ToggleColorMode from './ToggleColorMode';
import logo from '../assets/logo-white.png';
import config from '../utils/Constants';

const logoStyle = {
  width: '140px',
  height: 'auto',
  cursor: 'pointer',
};

function AppAppBar({ mode, toggleColorMode }) {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };
  const signInUrl = config.getSignInUrl();

  const handleNavClick = (sectionId) => {
    setOpen(false);
    if (pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) {
        window.scrollTo({ top: el.offsetTop - 128, behavior: 'smooth' });
      }
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: 0,
          bgcolor: 'transparent',
          backgroundImage: 'none',
          mt: 2,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              borderRadius: '999px',
              bgcolor:
                theme.palette.mode === 'light'
                  ? 'rgba(255, 255, 255, 0.4)'
                  : 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(24px)',
              maxHeight: 40,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow:
                theme.palette.mode === 'light'
                  ? `0 0 1px rgba(85, 166, 246, 0.1), 1px 1.5px 2px -1px rgba(85, 166, 246, 0.15), 4px 4px 12px -2.5px rgba(85, 166, 246, 0.15)`
                  : '0 0 1px rgba(2, 31, 59, 0.7), 1px 1.5px 2px -1px rgba(2, 31, 59, 0.65), 4px 4px 12px -2.5px rgba(2, 31, 59, 0.65)',
            })}
          >
            {/* Logo only on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: '-18px' }}>
              <img src={logo} style={logoStyle} alt="logo of cynthia" />
            </Box>

            {/* All nav items + auth buttons on the right */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <MenuItem onClick={() => handleNavClick('features')} sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">Features</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavClick('how-it-works')} sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">How It Works</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavClick('highlights')} sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">Use Cases</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavClick('pricing')} sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">Pricing</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavClick('faq')} sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">FAQ</Typography>
              </MenuItem>
              <MenuItem component={RouterLink} to="/life-sciences" sx={{ py: '6px', px: '12px' }}>
                <Typography variant="body2" color="text.primary">Life Sciences</Typography>
              </MenuItem>
              <Button
                color="primary"
                variant="text"
                size="small"
                component="a"
                href={signInUrl}
              >
                Sign in
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="small"
                component="a"
                href={signInUrl}
              >
                Sign up
              </Button>
            </Box>

            {/* Mobile hamburger */}
            <Box sx={{ display: { sm: '', md: 'none' } }}>
              <Button
                variant="text"
                color="primary"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ minWidth: '30px', p: '4px' }}
              >
                <MenuIcon style={{ color: 'white !important' }} />
              </Button>
              <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                <Box
                  sx={{
                    minWidth: '60dvw',
                    p: 2,
                    backgroundColor: 'background.paper',
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'end',
                      flexGrow: 1,
                    }}
                  >
                    <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />
                  </Box>
                  <MenuItem onClick={() => handleNavClick('features')}>Features</MenuItem>
                  <MenuItem onClick={() => handleNavClick('how-it-works')}>How It Works</MenuItem>
                  <MenuItem onClick={() => handleNavClick('highlights')}>Use Cases</MenuItem>
                  <MenuItem onClick={() => handleNavClick('pricing')}>Pricing</MenuItem>
                  <MenuItem onClick={() => handleNavClick('faq')}>FAQ</MenuItem>
                  <MenuItem component={RouterLink} to="/life-sciences">Life Sciences</MenuItem>
                  <Divider />
                  <MenuItem>
                    <Button
                      color="primary"
                      variant="contained"
                      component="a"
                      href={signInUrl}
                      sx={{ width: '100%' }}
                    >
                      Sign up
                    </Button>
                  </MenuItem>
                  <MenuItem>
                    <Button
                      color="primary"
                      variant="outlined"
                      component="a"
                      href={signInUrl}
                      sx={{ width: '100%' }}
                    >
                      Sign in
                    </Button>
                  </MenuItem>
                </Box>
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}

AppAppBar.propTypes = {
  mode: PropTypes.oneOf(['dark', 'light']).isRequired,
  toggleColorMode: PropTypes.func.isRequired,
};

export default AppAppBar;
